<template>
  <RevContainer class="mx-auto max-w-[760px]">
    <section class="flex flex-col gap-8">
      <h1 class="heading-1">{{ i18n(translations.heading) }}</h1>

      <div v-if="isRenewable" class="space-y-16">
        <p>
          {{ i18n(translations.generateNewLinkMessage) }}
        </p>

        <div class="space-y-32">
          <RevButton
            :disabled="hasBeenRenewed"
            full-width="adaptive"
            :loading="isLoading"
            variant="primary"
            @click="handleNewLinkGeneration"
          >
            {{ i18n(translations.generateNewLinkCta) }}
          </RevButton>

          <RevDivider />

          <h2 class="heading-3">
            {{ i18n(translations.changeYourMindTitle) }}
          </h2>
        </div>

        <BouyguesAssistanceInfoBlock />
      </div>

      <div v-else class="space-y-32">
        <p>
          {{ i18n(translations.magicLinkNotRenewable) }}
        </p>

        <BouyguesAssistanceInfoBlock />
      </div>
    </section>
  </RevContainer>
</template>

<script lang="ts" setup>
import type { NuxtError } from '#app'
import { computed, ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevDivider } from '@ds/components/Divider'

import type { MagicLinkError } from '../../BouyguesMagicLinkLayout.types'

import translations from './ExpiredError.translations'
import BouyguesAssistanceInfoBlock from './components/BouyguesAssistanceInfoBlock/BouyguesAssistanceInfoBlock.vue'

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const { openErrorToast, openSuccessToast } = useTheToast()

const isLoading = ref(false)
const hasBeenRenewed = ref(false)

const props = defineProps<{
  magicLinkId: string
  magicLinkError: NuxtError<MagicLinkError>
}>()

const isRenewable = computed(() => {
  return !!props.magicLinkError.data?.backmarket?.isRenewable
})

async function handleNewLinkGeneration() {
  isLoading.value = true

  try {
    await $httpFetch(
      '/api/mobile-plan/in-store/v1/magic-links/:magicLinkId/renew',
      {
        method: 'POST',
        pathParams: {
          magicLinkId: props.magicLinkId,
        },
      },
    )
    openSuccessToast({
      title: i18n(translations.renewalSuccessToastTitle),
      content: i18n(translations.renewalSuccessToastContent),
    })

    hasBeenRenewed.value = true
  } catch (e) {
    openErrorToast()
    throw e
  } finally {
    isLoading.value = false
  }
}
</script>
